import type { ChatToolEvent, StreamingToolEvent } from "@/types/chat.type";

export const ATTACH_VIDEO_TOOL = "AttachVideoFromLink";

/** One link of an AttachVideoFromLink batch, as the rail shows it. */
export type AttachedVideoLine = {
  key: string;
  ok: boolean;
  name: string;
  /** Why it failed, or that it was already there. */
  detail: string | null;
  documentId: string | null;
  /** Attached by this call (not already there). */
  isNew: boolean;
  /** The post it came from. */
  url: string | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/** The per-link outcome of a finished AttachVideoFromLink call; [] for anything else. */
export function attachedVideosOf(event: ChatToolEvent | StreamingToolEvent): AttachedVideoLine[] {
  if (event.toolName !== ATTACH_VIDEO_TOOL || !isRecord(event.result)) return [];
  const videos = event.result.videos;
  if (!Array.isArray(videos)) return [];
  return videos.flatMap((video, i): AttachedVideoLine[] => {
    if (!isRecord(video)) return [];
    const url = text(video.url) ?? "";
    if (video.success === true) {
      const documentId = text(video.documentId);
      const already = video.alreadyAttached === true;
      return [
        {
          key: documentId ?? `ok-${i}`,
          ok: true,
          name: (text(video.fileName) ?? hostOf(url) ?? "Video").replace(/\.(mp4|mov|m4v|webm|mkv)$/i, ""),
          detail: already ? "already attached" : null,
          documentId,
          isNew: !already,
          url: url || null,
        },
      ];
    }
    return [
      {
        key: `failed-${i}`,
        ok: false,
        name: hostOf(url) ?? (url || "Link"),
        detail: text(video.message),
        documentId: null,
        isNew: false,
        url: url || null,
      },
    ];
  });
}

/** Documents a finished AttachVideoFromLink call added (not ones already there). */
export function newlyAttachedDocumentIds(event: ChatToolEvent | StreamingToolEvent): string[] {
  return attachedVideosOf(event).flatMap((v) => (v.isNew && v.documentId ? [v.documentId] : []));
}
